import React from 'react';
import { Clock, X, Check, Copy, User } from 'lucide-react';
import { getSignedMediaUrl } from '@/lib/utils';

interface UserInfo {
    id?: string;
    name?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    profile_image_id?: string;
}

interface TransferStatusPanelProps {
    role: 'SENDER' | 'RECEIVER' | null;
    displayStatus: string;
    isPending: boolean;
    isCompleted: boolean;
    countdown: string;
    copied: boolean;
    isAccepting: boolean;
    bookingPrice?: number | string;
    sender?: UserInfo | null;
    receiver?: UserInfo | null;
    onCopyLink: () => void;
    onCancelClick: () => void;
    onAccept: () => void;
    onViewTicket: () => void;
}

const STATUS_STYLES: Record<string, string> = {
    'PENDING': 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20',
    'COMPLETED': 'text-green-500 bg-green-500/10 border-green-500/20',
    'EXPIRED': 'text-red-500 bg-red-500/10 border-red-500/20',
    'CANCELLED': 'text-red-500 bg-red-500/10 border-red-500/20',
    'INVALIDATED': 'text-red-500 bg-red-500/10 border-red-500/20',
};

// Helper component for displaying user info
const UserInfoDisplay: React.FC<{ user: UserInfo; label: string }> = ({ user, label }) => {
    // Construct name: prefer 'name' property, fallback to 'first_name last_name'
    const displayName = user.name ||
        ((user.first_name || user.last_name) ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'Unknown User');

    return (
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50">
            <p className="text-xs text-gray-500 uppercase mb-3">{label}</p>
            <div className="flex items-center gap-3">
                {user.profile_image_id ? (
                    <img
                        src={getSignedMediaUrl(user.profile_image_id).url}
                        alt={displayName}
                        className="w-10 h-10 rounded-full object-cover border border-gray-600"
                    />
                ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center border border-gray-600">
                        <User className="w-5 h-5 text-gray-400" />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{displayName}</p>
                    {user.email && (
                        <p className="text-gray-400 text-xs truncate">{user.email}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export const TransferStatusPanel: React.FC<TransferStatusPanelProps> = ({
    role,
    displayStatus,
    isPending,
    isCompleted,
    countdown,
    copied,
    isAccepting,
    bookingPrice,
    sender,
    receiver,
    onCopyLink,
    onCancelClick,
    onAccept,
    onViewTicket,
}) => {
    // Determine which user info to show based on role and status
    const shouldShowReceiverInfo = role === 'SENDER' && isCompleted && receiver;
    const shouldShowSenderInfo = role === 'RECEIVER' && (isPending || isCompleted) && sender;

    return (
        <div className="w-[320px] flex-shrink-0 bg-gray-900 border border-gray-800 rounded-2xl p-6 flex flex-col gap-5">
            <h3 className="text-lg font-bold text-white">Transfer Details</h3>

            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase border w-fit ${STATUS_STYLES[displayStatus] || STATUS_STYLES['PENDING']}`}>
                {displayStatus}
            </span>

            {/* Booking Price - Always shown */}
            {bookingPrice !== undefined && (
                <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Booking Price</p>
                    <p className="text-2xl font-bold text-white">
                        {bookingPrice}
                    </p>
                </div>
            )}

            {isPending && (
                <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Expires In</p>
                    <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-violet-500" />
                        <p className="text-3xl font-mono font-bold text-white">{countdown}</p>
                    </div>
                </div>
            )}

            {/* Sender Info - Shown to Receiver when pending or completed */}
            {shouldShowSenderInfo && (
                <UserInfoDisplay user={sender} label="Transferred From" />
            )}

            {/* Receiver Info - Shown to Sender when completed */}
            {shouldShowReceiverInfo && (
                <UserInfoDisplay user={receiver} label="Transferred To" />
            )}

            {/* Action Buttons */}
            <div className="mt-auto flex flex-col gap-3">
                {role === 'SENDER' && isPending && (
                    <>
                        <button
                            onClick={onCancelClick}
                            className="w-full py-3 bg-red-500/20 text-red-500 border border-red-500/40 font-bold rounded-xl hover:bg-red-500/30 transition flex items-center justify-center gap-2"
                        >
                            <X className="w-4 h-4" /> Cancel Transfer
                        </button>
                        <button
                            onClick={onCopyLink}
                            className={`w-full py-3 font-bold rounded-xl transition flex items-center justify-center gap-2 ${copied
                                ? 'bg-green-600 text-white'
                                : 'bg-violet-600 text-white hover:bg-violet-500'}`}
                        >
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            {copied ? 'Copied!' : 'Copy Link'}
                        </button>
                        <button
                            onClick={onViewTicket}
                            className="w-full py-3 bg-gray-800 border border-gray-700 text-gray-300 font-bold rounded-xl hover:bg-gray-700 transition"
                        >
                            View Ticket
                        </button>
                    </>
                )}

                {role === 'RECEIVER' && isPending && (
                    <button
                        onClick={onAccept}
                        disabled={isAccepting}
                        className="w-full py-4 bg-violet-600 text-white font-bold rounded-xl hover:bg-violet-500 transition shadow-lg shadow-violet-900/30 flex items-center justify-center gap-2 text-lg"
                    >
                        {isAccepting ? "Processing..." : "Accept Ticket"}
                    </button>
                )}
            </div>
        </div>
    );
};

export default TransferStatusPanel;
